import { useEffect, useRef, useState } from "react";
import { Lock, Plus, Trash2, Upload, Play, Save, GripVertical, Pencil, X, Check } from "lucide-react";
import {
  verifyPasscode, getWork, getNetwork, createWork, deleteWork, createClient, deleteClient,
  uploadImage, resolveSrc, ytId, ytThumb, getSettings, updateSettings,
  reorderWork, reorderNetwork, updateClient, updateWork,
} from "../lib/api";

const CATS = [
  { id: "thumbnail", label: "Thumbnail" },
  { id: "short", label: "Short" },
  { id: "longform", label: "Long-form" },
  { id: "design", label: "Design" },
];
const VIDEO_CATS = ["short", "longform"];
const inputStyle = { background: "#fff", border: "2.5px solid var(--black)", color: "var(--black)" };

const TEXT_FIELDS = [
  { key: "desktop_quote", label: "DESKTOP QUOTE (home poster)" },
  { key: "network_title", label: "NETWORK — TITLE" },
  { key: "network_subtitle", label: "NETWORK — SUBTITLE" },
  { key: "contact_kicker", label: "CONTACT — SMALL LABEL" },
  { key: "contact_title", label: "CONTACT — HEADLINE" },
];

function Field({ label, ...props }) {
  return (
    <label className="block mb-2.5">
      <span className="font-pixel text-[17px] block mb-1" style={{ color: "var(--black)" }}>{label}</span>
      <input {...props} className="w-full px-2.5 py-1.5 text-[14px] outline-none" style={inputStyle} />
    </label>
  );
}

function useDragList(items, setItems, onReorderSave) {
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => {
    if (dragIndex.current === null || i === dragOverIndex.current) return;
    dragOverIndex.current = i;
    const next = [...items];
    const dragged = next.splice(dragIndex.current, 1)[0];
    next.splice(i, 0, dragged);
    dragIndex.current = i;
    setItems(next);
  };
  const onDragEnd = () => {
    onReorderSave(items.map((it, idx) => ({ id: it.id, order: idx + 1 })));
    dragIndex.current = null;
    dragOverIndex.current = null;
  };

  return { onDragStart, onDragEnter, onDragEnd };
}

export default function AdminApp() {
  const [pass, setPass] = useState("");
  const [authed, setAuthed] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("work");
  const [work, setWork] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});
  const [wf, setWf] = useState({ category: "thumbnail", title: "", subtitle: "", image_url: "", youtube_url: "", link: "" });
  const [cf, setCf] = useState({ name: "", followers: "", logo_url: "", link: "" });
  const [upWork, setUpWork] = useState(false);
  const [upLogo, setUpLogo] = useState(false);
  const workFileRef = useRef(null);
  const logoFileRef = useRef(null);

  // Inline network editing
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [upEditLogo, setUpEditLogo] = useState(false);
  const editLogoRef = useRef(null);

  // Inline work editing
  const [editingWorkId, setEditingWorkId] = useState(null);
  const [editWorkDraft, setEditWorkDraft] = useState({});
  const [upEditWorkImg, setUpEditWorkImg] = useState(false);
  const editWorkFileRef = useRef(null);

  const isVideo = VIDEO_CATS.includes(wf.category);

  const refresh = () => { getWork().then(setWork); getNetwork().then(setClients); getSettings().then(setSettings); };
  useEffect(() => { if (authed) refresh(); }, [authed]);

  const login = async () => {
    setErr("");
    try { await verifyPasscode(pass); setAuthed(true); } catch { setErr("ACCESS DENIED"); }
  };

  const onWorkFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setUpWork(true);
    try { const res = await uploadImage(file, pass); setWf((w) => ({ ...w, image_url: res.url })); }
    catch { setErr("UPLOAD FAILED"); }
    setUpWork(false);
  };
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setUpLogo(true);
    try { const res = await uploadImage(file, pass); setCf((c) => ({ ...c, logo_url: res.url })); }
    catch { setErr("UPLOAD FAILED"); }
    setUpLogo(false);
  };

  const addWork = async () => {
    if (!wf.title) return setErr("TITLE REQUIRED");
    if (isVideo && !ytId(wf.youtube_url)) return setErr("VALID YOUTUBE LINK REQUIRED");
    if (!isVideo && !wf.image_url) return setErr("UPLOAD AN IMAGE FIRST");
    setErr("");
    await createWork(wf, pass);
    setWf({ category: wf.category, title: "", subtitle: "", image_url: "", youtube_url: "", link: "" });
    if (workFileRef.current) workFileRef.current.value = "";
    refresh();
  };
  const addClient = async () => {
    if (!cf.name) return setErr("NAME REQUIRED");
    if (!cf.logo_url) return setErr("UPLOAD A LOGO FIRST");
    setErr(""); await createClient(cf, pass);
    setCf({ name: "", followers: "", logo_url: "", link: "" });
    if (logoFileRef.current) logoFileRef.current.value = "";
    refresh();
  };
  const saveText = async () => {
    setErr(""); setMsg("");
    try { const res = await updateSettings(settings, pass); setSettings(res); setMsg("SAVED ✓"); setTimeout(() => setMsg(""), 2000); }
    catch { setErr("SAVE FAILED"); }
  };

  const workDrag = useDragList(work, setWork, (ordered) => reorderWork(ordered, pass).catch(() => {}));
  const networkDrag = useDragList(clients, setClients, (ordered) => reorderNetwork(ordered, pass).catch(() => {}));

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditDraft({ name: c.name, followers: c.followers, link: c.link || "", logo_url: c.logo_url || "" });
    setErr("");
  };
  const cancelEdit = () => { setEditingId(null); setErr(""); };
  const saveEdit = async (id) => {
    setErr("");
    try {
      await updateClient(id, editDraft, pass);
      setClients(prev => prev.map(c => c.id === id ? { ...c, ...editDraft } : c));
      setEditingId(null);
    } catch { setErr("SAVE FAILED"); }
  };
  const onEditLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpEditLogo(true); setErr("");
    try { const res = await uploadImage(file, pass); setEditDraft(d => ({ ...d, logo_url: res.url })); }
    catch { setErr("UPLOAD FAILED"); }
    setUpEditLogo(false);
  };

  const startEditWork = (it) => {
    setEditingWorkId(it.id);
    setEditWorkDraft({ title: it.title, subtitle: it.subtitle || "", image_url: it.image_url || "", youtube_url: it.youtube_url || "" });
    setErr("");
  };
  const cancelEditWork = () => { setEditingWorkId(null); setErr(""); };
  const saveEditWork = async (it) => {
    setErr("");
    const payload = { title: editWorkDraft.title, subtitle: editWorkDraft.subtitle };
    if (VIDEO_CATS.includes(it.category)) payload.youtube_url = editWorkDraft.youtube_url;
    else payload.image_url = editWorkDraft.image_url;
    try {
      await updateWork(it.id, payload, pass);
      setWork(prev => prev.map(w => w.id === it.id ? { ...w, ...payload } : w));
      setEditingWorkId(null);
    } catch { setErr("SAVE FAILED"); }
  };
  const onEditWorkImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpEditWorkImg(true); setErr("");
    try { const res = await uploadImage(file, pass); setEditWorkDraft(d => ({ ...d, image_url: res.url })); }
    catch { setErr("UPLOAD FAILED"); }
    setUpEditWorkImg(false);
  };

  const fetchYtAvatar = async () => {
    if (!editDraft.link) { setErr("ADD A YOUTUBE LINK FIRST"); return; }
    setUpEditLogo(true); setErr("");
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
      const { data } = await (await import("axios")).default.get(
        `${BACKEND}/api/youtube-avatar`,
        { params: { url: editDraft.link }, headers: { "X-Admin-Passcode": pass } }
      );
      setEditDraft(d => ({ ...d, logo_url: data.url }));
    } catch { setErr("COULD NOT FETCH — TRY MANUAL UPLOAD"); }
    setUpEditLogo(false);
  };

  if (!authed) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8" style={{ background: "var(--red)" }} data-testid="admin-app">
        <div className="w-16 h-16 flex items-center justify-center mb-4 retro-border" style={{ background: "var(--black)", boxShadow: "5px 5px 0 rgba(0,0,0,0.5)" }}>
          <Lock size={28} strokeWidth={2.5} color="var(--cream)" />
        </div>
        <h2 className="font-display text-3xl leading-none mb-1" style={{ color: "var(--black)" }}>STUDIO ACCESS</h2>
        <p className="font-pixel text-[18px] mb-4" style={{ color: "var(--black)" }}>&gt; enter passcode</p>
        <input data-testid="admin-passcode-input" type="password" value={pass}
          onChange={(e) => setPass(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
          placeholder="********" className="w-56 text-center px-3 py-2 text-[15px] outline-none mb-3 font-pixel" style={inputStyle} />
        {err && <div className="font-pixel text-[18px] mb-2" style={{ color: "var(--black)", background: "var(--cream)", padding: "2px 8px" }}>{err}</div>}
        <button data-testid="admin-login-btn" onClick={login}
          className="w-56 py-2.5 font-display text-[18px] retro-btn" style={{ background: "var(--black)", color: "var(--cream)" }}>
          UNLOCK
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: "var(--red)" }} data-testid="admin-app">
      <div className="flex gap-1.5 p-2 shrink-0" style={{ borderBottom: "3px solid var(--black)" }}>
        {[["work", "WORK"], ["network", "NETWORK"], ["text", "TEXT"]].map(([id, l]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => { setTab(id); setErr(""); setMsg(""); }}
            className="px-3 py-1.5 font-display text-[16px] leading-none"
            style={{ background: tab === id ? "var(--black)" : "var(--cream)", color: tab === id ? "var(--cream)" : "var(--black)", border: "2.5px solid var(--black)" }}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 jry-scroll overflow-y-auto p-3">
        {err && <div className="font-pixel text-[18px] mb-3 inline-block px-2" style={{ background: "var(--black)", color: "var(--cream)" }}>{err}</div>}
        {msg && <div className="font-pixel text-[18px] mb-3 inline-block px-2" style={{ background: "var(--black)", color: "var(--cream)" }}>{msg}</div>}

        {tab === "work" && (
          <>
            <div className="retro-border p-3 mb-4" style={{ background: "var(--cream)" }}>
              <div className="font-display text-[18px] mb-2" style={{ color: "var(--black)" }}>ADD WORK</div>
              <label className="block mb-2.5">
                <span className="font-pixel text-[17px] block mb-1">CATEGORY</span>
                <select data-testid="admin-work-category" value={wf.category}
                  onChange={(e) => setWf({ ...wf, category: e.target.value, image_url: "", youtube_url: "" })}
                  className="w-full px-2.5 py-1.5 text-[14px] outline-none" style={inputStyle}>
                  {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <Field label="TITLE" data-testid="admin-work-title" value={wf.title} onChange={(e) => setWf({ ...wf, title: e.target.value })} />
              <Field label="SUBTITLE" value={wf.subtitle} onChange={(e) => setWf({ ...wf, subtitle: e.target.value })} />

              {isVideo ? (
                <>
                  <Field label="YOUTUBE LINK (video or short)" data-testid="admin-work-youtube" placeholder="https://youtube.com/watch?v=…"
                    value={wf.youtube_url} onChange={(e) => setWf({ ...wf, youtube_url: e.target.value })} />
                  {ytId(wf.youtube_url) && (
                    <div className="mb-2.5 flex items-center gap-2">
                      <img src={ytThumb(wf.youtube_url)} alt="preview" className="w-24 h-14 object-cover" style={{ border: "2px solid var(--black)" }} />
                      <span className="font-pixel text-[16px]" style={{ color: "var(--black)" }}><Play size={13} className="inline" /> video ready</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <span className="font-pixel text-[17px] block mb-1">UPLOAD IMAGE</span>
                  <input ref={workFileRef} data-testid="admin-work-file" type="file" accept="image/*" onChange={onWorkFile}
                    className="w-full text-[13px] mb-2 font-pixel" style={{ color: "var(--black)" }} />
                  {upWork && <div className="font-pixel text-[17px] mb-2">UPLOADING…</div>}
                  {wf.image_url && (
                    <img src={resolveSrc(wf.image_url)} alt="preview" className="w-32 h-20 object-cover mb-2.5" style={{ border: "2px solid var(--black)" }} />
                  )}
                </>
              )}

              <Field label="LINK (OPTIONAL)" value={wf.link} onChange={(e) => setWf({ ...wf, link: e.target.value })} />
              <button data-testid="admin-add-work-btn" onClick={addWork} disabled={upWork}
                className="flex items-center gap-1.5 px-3 py-2 font-display text-[15px] retro-btn" style={{ background: "var(--black)", color: "var(--cream)", opacity: upWork ? 0.5 : 1 }}>
                {isVideo ? <Plus size={16} strokeWidth={3} /> : <Upload size={16} strokeWidth={3} />} ADD WORK
              </button>
            </div>

            <div className="font-pixel text-[12px] mb-2 opacity-70" style={{ color: "var(--black)" }}>
              ≡ DRAG TO REORDER
            </div>
            <div className="space-y-2">
              {work.map((it, i) => {
                const thumb = it.image_url ? resolveSrc(it.image_url) : ytThumb(it.youtube_url);
                const isEditingWork = editingWorkId === it.id;
                const isVid = VIDEO_CATS.includes(it.category);
                return (
                  <div key={it.id} className="retro-border" style={{ background: "var(--cream)" }}>
                    <div
                      draggable={!isEditingWork}
                      onDragStart={() => !isEditingWork && workDrag.onDragStart(i)}
                      onDragEnter={() => !isEditingWork && workDrag.onDragEnter(i)}
                      onDragEnd={() => !isEditingWork && workDrag.onDragEnd()}
                      onDragOver={(e) => e.preventDefault()}
                      className={`flex items-center gap-2 p-2 ${!isEditingWork ? "cursor-grab active:cursor-grabbing" : ""}`}>
                      <GripVertical size={16} strokeWidth={2} style={{ color: "var(--black)", opacity: 0.4, flexShrink: 0 }} />
                      {thumb
                        ? <img src={thumb} alt="" className="w-12 h-9 object-cover shrink-0" style={{ border: "2px solid var(--black)" }} />
                        : <div className="w-12 h-9 flex items-center justify-center shrink-0" style={{ border: "2px solid var(--black)" }}><Play size={14} /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] leading-none truncate" style={{ color: "var(--black)" }}>{it.title}</div>
                        <div className="font-pixel text-[15px] leading-none" style={{ color: "var(--red-dark)" }}>
                          {it.category}{ytId(it.youtube_url) ? " · ▶ video" : ""}
                        </div>
                      </div>
                      <button onClick={() => isEditingWork ? cancelEditWork() : startEditWork(it)}
                        className="p-1.5 shrink-0" style={{ background: isEditingWork ? "var(--black)" : "var(--cream)", color: isEditingWork ? "var(--cream)" : "var(--black)", border: "2px solid var(--black)" }}>
                        {isEditingWork ? <X size={15} strokeWidth={2.5} /> : <Pencil size={15} strokeWidth={2.5} />}
                      </button>
                      <button data-testid={`admin-del-work-${it.id}`} onClick={async () => { await deleteWork(it.id, pass); refresh(); }}
                        className="p-1.5 shrink-0" style={{ background: "var(--red)", color: "var(--cream)", border: "2px solid var(--black)" }}>
                        <Trash2 size={16} strokeWidth={2.5} />
                      </button>
                    </div>

                    {isEditingWork && (
                      <div className="px-3 pb-3 border-t-2" style={{ borderColor: "var(--black)" }}>
                        <div className="grid grid-cols-2 gap-2 mt-2 mb-2">
                          <label className="col-span-2">
                            <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>TITLE</span>
                            <input value={editWorkDraft.title} onChange={e => setEditWorkDraft(d => ({ ...d, title: e.target.value }))}
                              className="w-full px-2 py-1 text-[13px] outline-none font-pixel" style={inputStyle} />
                          </label>
                          <label className="col-span-2">
                            <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>SUBTITLE</span>
                            <input value={editWorkDraft.subtitle} onChange={e => setEditWorkDraft(d => ({ ...d, subtitle: e.target.value }))}
                              className="w-full px-2 py-1 text-[13px] outline-none font-pixel" style={inputStyle} />
                          </label>
                        </div>

                        {isVid ? (
                          <label className="block mb-2">
                            <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>YOUTUBE URL</span>
                            <input value={editWorkDraft.youtube_url} onChange={e => setEditWorkDraft(d => ({ ...d, youtube_url: e.target.value }))}
                              className="w-full px-2 py-1 text-[13px] outline-none font-pixel" style={inputStyle} />
                            {ytId(editWorkDraft.youtube_url) && (
                              <img src={ytThumb(editWorkDraft.youtube_url)} alt="preview"
                                className="w-20 h-12 object-cover mt-1" style={{ border: "2px solid var(--black)" }} />
                            )}
                          </label>
                        ) : (
                          <div className="mb-2">
                            <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>IMAGE</span>
                            <input ref={editWorkFileRef} type="file" accept="image/*" className="hidden" onChange={onEditWorkImageFile} />
                            <div className="flex items-center gap-2">
                              {editWorkDraft.image_url && (
                                <img src={resolveSrc(editWorkDraft.image_url)} alt="preview"
                                  className="w-16 h-10 object-cover shrink-0" style={{ border: "2px solid var(--black)" }} />
                              )}
                              <button type="button" onClick={() => editWorkFileRef.current?.click()} disabled={upEditWorkImg}
                                className="flex items-center gap-1 px-2 py-1 font-pixel text-[12px]"
                                style={{ ...inputStyle, cursor: "pointer", opacity: upEditWorkImg ? 0.6 : 1 }}>
                                <Upload size={11} strokeWidth={2.5} />
                                {upEditWorkImg ? "UPLOADING…" : editWorkDraft.image_url ? "CHANGE" : "UPLOAD"}
                              </button>
                            </div>
                          </div>
                        )}

                        <button onClick={() => saveEditWork(it)}
                          className="flex items-center gap-1.5 px-3 py-1.5 font-display text-[14px]"
                          style={{ background: "var(--black)", color: "var(--cream)" }}>
                          <Check size={14} strokeWidth={3} /> SAVE
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "network" && (
          <>
            <div className="retro-border p-3 mb-4" style={{ background: "var(--cream)" }}>
              <div className="font-display text-[18px] mb-2" style={{ color: "var(--black)" }}>ADD CLIENT</div>
              <Field label="NAME" data-testid="admin-client-name" value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} />
              <Field label="FOLLOWERS (e.g. 5M+)" data-testid="admin-client-followers" value={cf.followers} onChange={(e) => setCf({ ...cf, followers: e.target.value })} />
              <span className="font-pixel text-[17px] block mb-1">UPLOAD LOGO / AVATAR</span>
              <input ref={logoFileRef} data-testid="admin-client-logo-file" type="file" accept="image/*" onChange={onLogoFile}
                className="w-full text-[13px] mb-2 font-pixel" style={{ color: "var(--black)" }} />
              {upLogo && <div className="font-pixel text-[17px] mb-2">UPLOADING…</div>}
              {cf.logo_url && (
                <img src={resolveSrc(cf.logo_url)} alt="preview" className="w-16 h-16 rounded-full object-cover mb-2.5" style={{ border: "2px solid var(--black)" }} />
              )}
              <Field label="LINK (OPTIONAL)" value={cf.link} onChange={(e) => setCf({ ...cf, link: e.target.value })} />
              <button data-testid="admin-add-client-btn" onClick={addClient} disabled={upLogo}
                className="flex items-center gap-1.5 px-3 py-2 font-display text-[15px] retro-btn" style={{ background: "var(--black)", color: "var(--cream)", opacity: upLogo ? 0.5 : 1 }}>
                <Upload size={16} strokeWidth={3} /> ADD CLIENT
              </button>
            </div>

            <div className="font-pixel text-[12px] mb-2 opacity-70" style={{ color: "var(--black)" }}>
              ≡ DRAG TO REORDER
            </div>
            <div className="space-y-2">
              {clients.map((c, i) => {
                const isEditing = editingId === c.id;
                return (
                  <div key={c.id} className="retro-border" style={{ background: "var(--cream)" }}>
                    {/* Row */}
                    <div
                      draggable={!isEditing}
                      onDragStart={() => !isEditing && networkDrag.onDragStart(i)}
                      onDragEnter={() => !isEditing && networkDrag.onDragEnter(i)}
                      onDragEnd={() => !isEditing && networkDrag.onDragEnd()}
                      onDragOver={(e) => e.preventDefault()}
                      className={`flex items-center gap-2 p-2 ${!isEditing ? "cursor-grab active:cursor-grabbing" : ""}`}>
                      <GripVertical size={16} strokeWidth={2} style={{ color: "var(--black)", opacity: 0.4, flexShrink: 0 }} />
                      {c.logo_url
                        ? <img src={resolveSrc(c.logo_url)} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" style={{ border: "2px solid var(--black)" }} />
                        : <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center font-display text-[16px]"
                            style={{ background: "var(--black)", color: "var(--cream)", border: "2px solid var(--black)" }}>
                            {c.name?.[0]?.toUpperCase()}
                          </div>}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] leading-none truncate" style={{ color: "var(--black)" }}>{c.name}</div>
                        <div className="font-pixel text-[15px] leading-none" style={{ color: "var(--red-dark)" }}>{c.followers}</div>
                      </div>
                      <button onClick={() => isEditing ? cancelEdit() : startEdit(c)}
                        className="p-1.5 shrink-0" style={{ background: isEditing ? "var(--black)" : "var(--cream)", color: isEditing ? "var(--cream)" : "var(--black)", border: "2px solid var(--black)" }}>
                        {isEditing ? <X size={15} strokeWidth={2.5} /> : <Pencil size={15} strokeWidth={2.5} />}
                      </button>
                      <button data-testid={`admin-del-client-${c.id}`} onClick={async () => { await deleteClient(c.id, pass); refresh(); }}
                        className="p-1.5 shrink-0" style={{ background: "var(--red)", color: "var(--cream)", border: "2px solid var(--black)" }}>
                        <Trash2 size={16} strokeWidth={2.5} />
                      </button>
                    </div>

                    {/* Inline edit panel */}
                    {isEditing && (
                      <div className="px-3 pb-3 border-t-2" style={{ borderColor: "var(--black)" }}>
                        <div className="grid grid-cols-2 gap-2 mt-2 mb-2">
                          {[["NAME", "name"], ["FOLLOWERS", "followers"], ["LINK", "link"]].map(([label, key]) => (
                            <label key={key} className={key === "link" ? "col-span-2" : ""}>
                              <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>{label}</span>
                              <input value={editDraft[key]} onChange={e => setEditDraft(d => ({ ...d, [key]: e.target.value }))}
                                className="w-full px-2 py-1 text-[13px] outline-none font-pixel" style={inputStyle} />
                            </label>
                          ))}
                        </div>
                        <div className="mb-2">
                          <span className="font-pixel text-[12px] block mb-0.5" style={{ color: "var(--black)" }}>LOGO</span>
                          <input ref={editLogoRef} type="file" accept="image/*" className="hidden" onChange={onEditLogoFile} />
                          <div className="flex items-center gap-2 flex-wrap">
                            {editDraft.logo_url && (
                              <img src={resolveSrc(editDraft.logo_url)} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" style={{ border: "2px solid var(--black)" }} />
                            )}
                            <button type="button" onClick={() => editLogoRef.current?.click()} disabled={upEditLogo}
                              className="flex items-center gap-1 px-2 py-1 font-pixel text-[12px]"
                              style={{ ...inputStyle, cursor: "pointer", opacity: upEditLogo ? 0.6 : 1 }}>
                              <Upload size={11} strokeWidth={2.5} />
                              {upEditLogo ? "FETCHING…" : editDraft.logo_url ? "CHANGE" : "UPLOAD"}
                            </button>
                            <button type="button" onClick={fetchYtAvatar} disabled={upEditLogo}
                              title="Auto-fetch from YouTube link above"
                              className="flex items-center gap-1 px-2 py-1 font-pixel text-[12px]"
                              style={{ background: "var(--red)", color: "var(--cream)", border: "2px solid var(--black)", cursor: "pointer", opacity: upEditLogo ? 0.6 : 1 }}>
                              ▶ AUTO
                            </button>
                          </div>
                          <div className="font-pixel text-[10px] mt-0.5 opacity-60" style={{ color: "var(--black)" }}>AUTO pulls pic from the YouTube link above</div>
                        </div>
                        <button onClick={() => saveEdit(c.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 font-display text-[14px]"
                          style={{ background: "var(--black)", color: "var(--cream)" }}>
                          <Check size={14} strokeWidth={3} /> SAVE
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "text" && (
          <div className="retro-border p-3" style={{ background: "var(--cream)" }}>
            <div className="font-display text-[18px] mb-2" style={{ color: "var(--black)" }}>EDIT SITE TEXT</div>
            {TEXT_FIELDS.map((f) => (
              <label key={f.key} className="block mb-3">
                <span className="font-pixel text-[17px] block mb-1" style={{ color: "var(--black)" }}>{f.label}</span>
                <textarea
                  data-testid={`admin-text-${f.key}`}
                  rows={f.key === "desktop_quote" ? 3 : 1}
                  value={settings[f.key] ?? ""}
                  onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                  className="w-full px-2.5 py-1.5 text-[14px] outline-none resize-none" style={inputStyle}
                />
              </label>
            ))}
            <button data-testid="admin-save-text-btn" onClick={saveText}
              className="flex items-center gap-1.5 px-3 py-2 font-display text-[15px] retro-btn" style={{ background: "var(--black)", color: "var(--cream)" }}>
              <Save size={16} strokeWidth={3} /> SAVE TEXT
            </button>
            <p className="font-pixel text-[15px] mt-2" style={{ color: "var(--red-dark)" }}>
              &gt; reopen a window to see changes
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
